// graphql/client.tsx - REFACTORED
import React, { useMemo, useEffect, useRef } from 'react';
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  HttpLink,
  from,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { message } from 'antd';
import useSession from '@hooks/useSession';
import { getApiBase } from '@utils/runtimeEnv';
import { appInsights } from '../appInsights';
import type { LogoutReason } from './authAudit';

// Decodes a JWT's exp claim (seconds since epoch) without verifying the signature —
// only used here to log token freshness, never for auth decisions.
function getTokenExpiryMs(token: string): number | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');
    const payload = JSON.parse(atob(base64));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

const API_BASE = getApiBase();

type Props = Readonly<React.PropsWithChildren<{}>>;

// ============================================
// Helper Functions (extracted to reduce complexity)
// ============================================

const isAuthenticationError = (msg: string, code?: string): boolean => {
  return (
    code === 'UNAUTHENTICATED' ||
    msg.toLowerCase().includes('unauthorized') ||
    msg.toLowerCase().includes('not authenticated')
  );
};

type LogoutFn = (reason?: LogoutReason) => void;

const handleAuthenticationError = (accessToken: string | null, handleLogout: LogoutFn) => {
  message.error('Session expired. Please log in again.');
  setTimeout(() => handleLogout('SESSION_EXPIRED'), 1500);
};

const handleGraphQLError = (
  error: any,
  accessToken: string | null,
  handleLogout: LogoutFn
): boolean => {
  const { message: msg, extensions } = error;

  if (isAuthenticationError(msg, extensions?.code)) {
    handleAuthenticationError(accessToken, handleLogout);
    return true; // Stop processing
  }

  if (extensions?.code === 'FORBIDDEN') {
    message.error('You do not have permission to perform this action.');
    return false;
  }

  if (extensions?.code === 'BAD_USER_INPUT') {
    message.error('Invalid input. Please check your data.');
    return false;
  }

  return false;
};

const handleNetworkError = (
  networkError: any,
  accessToken: string | null,
  handleLogout: LogoutFn
) => {
  const statusCode = 'statusCode' in networkError ? networkError.statusCode : null;

  if (statusCode === 401) {
    handleAuthenticationError(accessToken, handleLogout);
    return;
  }

  if (statusCode === 403) {
    message.error('Access denied. Please contact your administrator.');
    return;
  }

  if (statusCode === 500) {
    message.error('Server error. Please try again later.');
    return;
  }

  if ('message' in networkError) {
    const netMsg = networkError.message.toLowerCase();
    if (netMsg.includes('failed to fetch') || netMsg.includes('network')) {
      message.error('Network error. Please check your connection.');
    }
  }
};

// ============================================
// Main Component
// ============================================

export default function ApolloWrapper({ children }: Props) {
  const { accessToken, handleLogout } = useSession();

  const [tokenReady, setTokenReady] = React.useState(false);

  useEffect(() => {
    if (accessToken) {
      const timer = setTimeout(() => {
        setTokenReady(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setTokenReady(false);
    }
  }, [accessToken]);

  // Diagnostic only (WTTP-1016) — logs whether the token was already expired by the time
  // a backgrounded tab regains focus, to confirm/rule out tab-throttling as the cause of
  // the recurring /graphql 401s before any behavioral fix is made. Reads accessToken via
  // this ref so the listener doesn't need to be re-registered on every token refresh.
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;
  const hiddenAtRef = useRef<number | null>(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }

      if (document.visibilityState === 'visible' && hiddenAtRef.current !== null) {
        const hiddenForMs = Date.now() - hiddenAtRef.current;
        hiddenAtRef.current = null;

        const token = accessTokenRef.current;
        const expiryMs = token ? getTokenExpiryMs(token) : null;
        const msPastExpiry = expiryMs !== null ? Date.now() - expiryMs : null;

        appInsights?.trackEvent({
          name: 'TabVisibilityTokenCheck',
          properties: {
            hiddenForMs: String(hiddenForMs),
            hasToken: String(Boolean(token)),
            tokenExpired: String(msPastExpiry !== null && msPastExpiry > 0),
            msPastExpiry: msPastExpiry !== null ? String(msPastExpiry) : 'unknown',
          },
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const client = useMemo(() => {
    if (!accessToken) {
      return null;
    }

    const httpLink = new HttpLink({
      uri: `${API_BASE}/graphql`,
      credentials: 'include',
    });

    const authLink = setContext((_, { headers }) => {
      if (!accessToken) {
        return { headers };
      }

      return {
        headers: {
          ...headers,
          Authorization: `Bearer ${accessToken}`,
        },
      };
    });

    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        for (const error of graphQLErrors) {
          const shouldStop = handleGraphQLError(error, accessToken, handleLogout);
          if (shouldStop) {
            return;
          }
        }
      }

      if (networkError) {
        handleNetworkError(networkError, accessToken, handleLogout);
      }
    });

    return new ApolloClient({
      link: from([errorLink, authLink, httpLink]),
      cache: new InMemoryCache({
        addTypename: true, // maintained for Apollo cache normalization
        typePolicies: {
          // MeterSwapCell.id is meter_computed_id, which is shared by all 9
          // Meter Swap Details rows for the same month. Without this, Apollo's
          // default normalization keys every cell as `MeterSwapCell:<id>`,
          // collapsing all 9 sibling rows into one cache entity so they all
          // display the same (last-written) value instead of their own.
          MeterSwapCell: {
            keyFields: false,
          },
        },
      }),
      connectToDevTools: process.env.NODE_ENV !== 'production',
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network',
          errorPolicy: 'all',
          nextFetchPolicy: 'cache-first',
        },
        query: {
          fetchPolicy: 'cache-first',
          errorPolicy: 'all',
        },
        mutate: {
          errorPolicy: 'all',
        },
      },
    });
  }, [accessToken, handleLogout]);

  if (!tokenReady || !client) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div className="spinner" />
        <p>Initializing Apollo Client...</p>
      </div>
    );
  }

  return <ApolloProvider client={client}>{children ?? null}</ApolloProvider>;
}